/* eslint-disable @typescript-eslint/no-magic-numbers */
import { FootPrintShapeType, } from "@buildwithflux/models";
import { enforceGridPrecisionInMeters } from "@buildwithflux/shared";
import { MathUtils } from "three";
export function circleShapeToSvgPath(shape) {
    const radius = enforceGridPrecisionInMeters(shape.radius, 5);
    return `<circle cx="${enforceGridPrecisionInMeters(shape.position.x)}" cy="${enforceGridPrecisionInMeters(-shape.position.y)}" r="${radius}" stroke-width="${enforceGridPrecisionInMeters(shape.strokeWidth)}" fill="none" />`;
}
export function lineShapeToSvgPath(shape) {
    return `<path d="M ${enforceGridPrecisionInMeters(shape.start.x)} ${enforceGridPrecisionInMeters(-shape.start.y)} L ${enforceGridPrecisionInMeters(shape.end.x)} ${enforceGridPrecisionInMeters(-shape.end.y)}" stroke-width="${enforceGridPrecisionInMeters(shape.strokeWidth)}" stroke-linecap="round" fill="none" />`;
}
export function polygonShapeToSvgPath(shape) {
    if (shape.vertices.length === 0)
        return "";
    const first = shape.vertices[0];
    const rest = shape.vertices.slice(1);
    const d = `M ${enforceGridPrecisionInMeters(first.x)} ${enforceGridPrecisionInMeters(-first.y)} ${rest
        .map((v) => `L ${enforceGridPrecisionInMeters(v.x)} ${enforceGridPrecisionInMeters(-v.y)}`)
        .join(" ")} Z`;
    if (shape.strokeWidth > 0) {
        return `<path d="${d}" fill="black" stroke="black" stroke-width="${enforceGridPrecisionInMeters(shape.strokeWidth)}" />`;
    }
    return `<path d="${d}" fill="black" stroke="none" />`;
}
export function arcShapeToSvgPath(shape) {
    const center = calculateCircumcenter(shape.start, shape.mid, shape.end);
    if (!center) {
        // Degenerate case: arc points are approximately colinear, becoming a straight line or a complete circle
        const dToMid = Math.sqrt((shape.mid.x - shape.start.x) ** 2 + (shape.mid.y - shape.start.y) ** 2);
        const dToEnd = Math.sqrt((shape.end.x - shape.start.x) ** 2 + (shape.end.y - shape.start.y) ** 2);
        return dToMid < dToEnd
            ? lineShapeToSvgPath({
                start: shape.start,
                end: shape.end,
                strokeWidth: shape.strokeWidth,
                layerUid: shape.layerUid,
                type: FootPrintShapeType.line,
                uid: shape.uid,
            })
            : circleShapeToSvgPath({
                position: shape.mid,
                radius: dToMid,
                strokeWidth: shape.strokeWidth,
                layerUid: shape.layerUid,
                type: FootPrintShapeType.circle,
                uid: shape.uid,
            });
    }
    // Determine arc sweep direction using cross product sign from start to mid
    const ax = shape.start.x - center.x;
    const ay = shape.start.y - center.y;
    const bx = shape.mid.x - center.x;
    const by = shape.mid.y - center.y;
    const cross = ax * by - ay * bx;
    const sweepFlag = cross < 0 ? 1 : 0;
    // Determine arc angle using dot product from start to mid
    const dot = ax * bx + ay * by;
    const r2 = (shape.start.x - center.x) ** 2 + (shape.start.y - center.y) ** 2;
    const cosTheta = dot / r2;
    const angle = Math.acos(MathUtils.clamp(cosTheta, -1, 1)); // Clamp to avoid NaN numerical problems
    const largeArcFlag = angle > Math.PI / 2 ? 1 : 0;
    const r = enforceGridPrecisionInMeters(Math.sqrt(r2));
    return `<path d="M ${enforceGridPrecisionInMeters(shape.start.x)} ${enforceGridPrecisionInMeters(-shape.start.y)} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${enforceGridPrecisionInMeters(shape.end.x)} ${enforceGridPrecisionInMeters(-shape.end.y)}" stroke-width="${enforceGridPrecisionInMeters(shape.strokeWidth)}" fill="none" />`;
}
// Note: The circumcenter will not exist if the triangle points form a straight line.
// Derivation references:
//   https://mathworld.wolfram.com/Circumcircle.html
//   https://ics.uci.edu/~eppstein/junkyard/circumcenter.html
// with simplified but equivalent calculation of determinants.
export function calculateCircumcenter(a, b, c) {
    const A = b.x - a.x;
    const B = b.y - a.y;
    const C = c.x - a.x;
    const D = c.y - a.y;
    const E = A * (b.x + a.x) + B * (b.y + a.y);
    const F = C * (c.x + a.x) + D * (c.y + a.y);
    const G = 2 * (A * D - B * C);
    // `G` approximates the area of the triangle abc, calculated using the cross product.
    // If it is too small, the points are either too close to a straight line (with zero area) or too close to each other,
    // so the circumcenter doesn't exist or its calculation is unstable.
    const epsilon = 10e-12;
    return Math.abs(G) < epsilon ? undefined : { x: (D * E - B * F) / G, y: (A * F - C * E) / G };
}
//# sourceMappingURL=shapeToSvgPath.js.map